import React, { useState } from "react";
import { warrantyService } from "../../services/warranty.service"; // ✅ Import your new service
import "./WarrantyCheck.css";

const WarrantyCheck = () => {
  const [serialNumber, setSerialNumber] = useState("");
  const [warrantyData, setWarrantyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const calculateRemainingMonths = (endDate) => {
    const today = new Date();
    const end = new Date(endDate);

    // If the warranty is already expired
    if (today > end) return 0;

    let months = (end.getFullYear() - today.getFullYear()) * 12;
    months -= today.getMonth();
    months += end.getMonth();
    return months <= 0 ? 0 : months;
  };

  const handleCheck = async (e) => {
    e.preventDefault();
    if (!serialNumber.trim()) return;

    setLoading(true);
    setError("");
    setWarrantyData(null);

    try {
      // ✅ Call the real backend API using your service
      const response = await warrantyService.checkWarranty(serialNumber);

      if (response.success && response.data) {
        const remaining = calculateRemainingMonths(
          response.data.warranty_end_date,
        );

        setWarrantyData({
          ...response.data,
          remainingMonths: remaining,
        });
      } else {
        throw new Error(response.message || "Invalid Serial Number.");
      }
    } catch (err) {
      // Handle Axios errors from the backend gracefully
      setError(
        err.response?.data?.message ||
          err.message ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="warranty-page-container">
      <div className="warranty-check-card">
        {/* HEADER SECTION */}
        <div className="warranty-header">
          <h2>Verify Your Product Warranty</h2>
          <p>
            Enter your 12-digit serial number to check authenticity and warranty
            status.
          </p>
        </div>

        {/* SEARCH FORM */}
        <form onSubmit={handleCheck} className="warranty-search-form">
          <input
            type="text"
            placeholder="Enter Serial Number (e.g. SN-BAT-102938)"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            className="warranty-input"
          />
          <button type="submit" className="warranty-btn" disabled={loading}>
            {loading ? "Checking..." : "Verify Product"}
          </button>
        </form>

        {/* ERROR STATE */}
        {error && (
          <div className="warranty-error">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* RESULT SECTION */}
        {warrantyData && (
          <div className="warranty-result-container fade-in">
            <div className="result-image-side">
              <div className="status-badge valid">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                Valid Product
              </div>
              <img
                src={
                  warrantyData.image_url ||
                  "https://via.placeholder.com/400x300?text=Product+Image"
                }
                alt={warrantyData.product_name}
              />
            </div>

            <div className="result-details-side">
              <h3 className="product-title">{warrantyData.product_name}</h3>
              <p className="product-id">
                SKU: {warrantyData.sku} | ID: {warrantyData.product_id}
              </p>

              <div className="months-remaining-card">
                <span className="months-number">
                  {warrantyData.remainingMonths}
                </span>
                <span className="months-text">
                  Months
                  <br />
                  Remaining
                </span>
              </div>

              <div className="timeline-details">
                <div className="detail-row">
                  <span className="detail-label">Purchase Date</span>
                  <span className="detail-value">
                    {formatDate(warrantyData.purchase_date)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Warranty Starts</span>
                  <span className="detail-value">
                    {formatDate(warrantyData.warranty_start_date)}
                  </span>
                </div>
                <div className="detail-row highlight-row">
                  <span className="detail-label">Warranty Ends</span>
                  <span className="detail-value">
                    {formatDate(warrantyData.warranty_end_date)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WarrantyCheck;
